import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LeadDataDialog } from './LeadDataDialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { PenLine, Loader2, Copy, Download, MessageCircle, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { PdfContractFillModal } from './PdfContractFillModal';
import { CcRequestFiles } from './CcRequestFiles';
import { CcCompaniesSetting } from './CcCompaniesSetting';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (iso) => { try { return new Date(iso).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; } };

export const SignatureRequestPanel = ({ leadId, clientPhone, clientName }) => {
  const [docs, setDocs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState('');
  const [templateFields, setTemplateFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [includeSepa, setIncludeSepa] = useState(false);
  const [formMode, setFormMode] = useState(false);
  const [fillOpen, setFillOpen] = useState(false);
  const [selectedName, setSelectedName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [lastLink, setLastLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(null);
  const [afterFill, setAfterFill] = useState(null);
  const checkData = async (next) => {
    try {
      const { data } = await axios.get(`${API_URL}/leads/${leadId}`);
      const l = data.lead || data;
      const miss = ['client_name', 'client_phone', 'client_nif', 'client_email'].filter((k) => !(l[k] || '').trim());
      if (miss.length) { setAfterFill(() => next); setMissing(miss); toast.error('Preencha os dados em falta antes do link de assinatura'); return; }
    } catch { /* segue; o servidor valida */ }
    next();
  };

  const load = useCallback(async () => {
    try {
      const [docsRes, reqRes] = await Promise.all([
        axios.get(`${API_URL}/knowledge`),
        axios.get(`${API_URL}/leads/${leadId}/signature-requests`),
      ]);
      setDocs((docsRes.data.items || []).filter((d) => d.signable));
      setRequests(reqRes.data.items || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  // Ao escolher um modelo, carrega os campos a preencher (overlay custom + AcroForm configurado)
  useEffect(() => {
    if (!selectedDoc) { setTemplateFields([]); setFieldValues({}); setIncludeSepa(false); return; }
    (async () => {
      try {
        const [infoRes, formRes] = await Promise.all([
          axios.get(`${API_URL}/knowledge/${selectedDoc}/pdf-info`).catch(() => ({ data: {} })),
          axios.get(`${API_URL}/knowledge/${selectedDoc}/form-fields`).catch(() => ({ data: {} })),
        ]);
        const overlay = (infoRes.data?.signature_fields || [])
          .filter((f) => f.type === 'custom')
          .map((f) => ({ key: f.key, label: f.label || f.key, group: f.group || null }));
        const formCfg = (formRes.data?.form_fields || [])
          .map((f) => ({ key: f.name, label: f.label || f.name, group: f.group || null }));
        setFormMode(Boolean(formRes.data?.has_form) || formCfg.length > 0);
        const all = [...formCfg, ...overlay];
        setTemplateFields(all);
        const init = {};
        all.forEach((f) => { init[f.key] = ''; });
        setFieldValues(init);
        setIncludeSepa(false);
      } catch {
        setTemplateFields([]);
      }
    })();
  }, [selectedDoc]);

  const customFields = templateFields;
  const hasSepa = templateFields.some((f) => f.group === 'sepa');
  const visibleCustom = customFields.filter((f) => f.group !== 'sepa' || includeSepa);

  const signUrl = (token) => `${window.location.origin}/sign/${token}`;

  const shorten = async (url) => {
    try {
      const res = await axios.post(`${API_URL}/shorten`, { url });
      return res.data?.short_url || url;
    } catch {
      return url;
    }
  };

  const generate = async () => {
    if (!selectedDoc) { toast.error('Escolhe um modelo'); return; }
    setGenerating(true);
    try {
      const payload = { document_id: selectedDoc, field_values: fieldValues, include_sepa: includeSepa };
      const res = await axios.post(`${API_URL}/leads/${leadId}/signature-requests`, payload);
      const full = signUrl(res.data.token);
      const url = await shorten(full);
      setLastLink(url);
      toast.success('Link de assinatura gerado');
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 422 && d?.missing) {
        setMissing(d.missing);
        toast.error(d.message);
      } else {
        toast.error((typeof d === 'string' && d) || 'Falha ao gerar link');
      }
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = (url) => { navigator.clipboard.writeText(url); toast.success('Link copiado'); };

  const copyShort = async (token) => { copyLink(await shorten(signUrl(token))); };

  const waLink = (url) => {
    const digits = (clientPhone || '').replace(/\D/g, '');
    const phone = digits ? (digits.startsWith('351') ? digits : `351${digits}`) : '';
    const msg = encodeURIComponent(`Olá ${clientName || ''}, por favor assine a autorização aqui: ${url}`);
    return phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
  };

  const downloadSigned = async (req) => {
    try {
      const res = await axios.get(`${API_URL}/signature-requests/${req.id}/download`, { responseType: 'blob' });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = u; a.download = `ASSINADO - ${req.document_name}.pdf`; a.click();
      URL.revokeObjectURL(u);
    } catch {
      toast.error('Falha ao descarregar');
    }
  };

  return (
    <Card className="rounded-2xl border-indigo-100" data-testid="signature-request-panel">
      <LeadDataDialog leadId={leadId} open={!!missing} onlyFields={missing || undefined} title="Dados em falta antes do link de assinatura"
        onClose={() => { setMissing(null); setAfterFill(null); }} onSaved={() => { setMissing(null); const n = afterFill; setAfterFill(null); setTimeout(n || generate, 300); }} />
      <CardHeader className="border-b border-indigo-100 pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2 text-indigo-900">
          <PenLine className="w-4 h-4 text-indigo-600" /> Pedir Assinatura
          <CcCompaniesSetting />
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : docs.length === 0 ? (
          <p className="text-xs text-slate-500">Ainda não há modelos assináveis. Marca um documento como assinável na <b>Base de Conhecimento</b>.</p>
        ) : (
          <>
            <Select value={selectedDoc} onValueChange={(v) => { setSelectedDoc(v); const d = docs.find((x) => x.id === v); setSelectedName(d ? `${(d.empresa || '').toUpperCase()} · ${d.name}` : ''); }}>
              <SelectTrigger data-testid="sign-doc-select"><SelectValue placeholder="Escolher modelo (ex: LEAD ENDESA)" /></SelectTrigger>
              <SelectContent>
                {docs.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{(d.empresa || '').toUpperCase()} · {d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {formMode ? (
              <Button onClick={() => checkData(() => setFillOpen(true))} disabled={!selectedDoc} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="open-fill-contract-btn">
                <PenLine className="w-4 h-4 mr-2" /> Preencher contrato de adesão
              </Button>
            ) : (
              <>
                {hasSepa && (
                  <label className="flex items-center gap-2 text-sm text-cyan-800 bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-2 cursor-pointer" data-testid="sign-sepa-toggle">
                    <input type="checkbox" className="h-4 w-4 accent-cyan-600" checked={includeSepa} onChange={(e) => setIncludeSepa(e.target.checked)} />
                    Com IBAN (autorização SEPA / débito direto)
                  </label>
                )}

                {visibleCustom.length > 0 && (
                  <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="sign-fill-form">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Dados do contrato</p>
                    {visibleCustom.map((f) => (
                      <div key={f.key}>
                        <label className="text-[11px] text-slate-500">{f.label}{f.group === 'sepa' ? ' (SEPA)' : ''}</label>
                        <Input
                          value={fieldValues[f.key] || ''}
                          onChange={(e) => setFieldValues((p) => ({ ...p, [f.key]: e.target.value.toUpperCase() }))}
                          placeholder={f.label}
                          className="h-9 uppercase placeholder:normal-case"
                          data-testid={`sign-field-${f.key}`}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <Button onClick={generate} disabled={generating || !selectedDoc} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="generate-sign-link-btn">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PenLine className="w-4 h-4 mr-2" />}Gerar Link de Assinatura
                </Button>
              </>
            )}

            {lastLink && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2">
                <p className="text-[11px] text-indigo-700 break-all">{lastLink}</p>
                <div className="flex gap-2">
                  <a href={waLink(lastLink)} target="_blank" rel="noreferrer" className="flex-1">
                    <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="share-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button>
                  </a>
                  <Button size="sm" variant="outline" onClick={() => copyLink(lastLink)} data-testid="copy-link-btn"><Copy className="w-4 h-4 mr-1" /> Copiar</Button>
                </div>
              </div>
            )}
          </>
        )}

        {requests.length > 0 && (
          <div className="pt-2 space-y-2">
            <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Pedidos</p>
            {requests.map((r) => (
              <div key={r.id} className="flex items-center gap-2 text-xs border border-slate-100 rounded-lg p-2" data-testid={`sign-request-${r.id}`}>
                {r.status === 'assinado'
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  : <Clock className="w-4 h-4 text-amber-500 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-700 truncate">{r.document_name}</p>
                  <p className="text-[10px] text-slate-400">{r.status === 'assinado' ? `Assinado ${fmt(r.signed_at)}` : 'Pendente'}</p>
                  <CcRequestFiles req={r} />
                </div>
                {r.status === 'assinado' ? (
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-indigo-600" onClick={() => downloadSigned(r)} title="Descarregar assinado" data-testid={`download-signed-${r.id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500" onClick={() => copyShort(r.token)} title="Copiar link">
                    <Copy className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <PdfContractFillModal
        open={fillOpen}
        onClose={() => setFillOpen(false)}
        documentId={selectedDoc}
        documentName={selectedName}
        leadId={leadId}
        onGenerated={load}
      />
    </Card>
  );
};
